import * as React from 'react';
import { XIcon } from '@phosphor-icons/react/X';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '../../../utils/cn';
import type { TitleFace } from './types';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    /**
     * Extra classes for the backdrop overlay (e.g. a blur).
     */
    overlayClassName?: string;
    /**
     * Inline style for the backdrop, e.g. a `clipPath` hole that keeps the
     * opening control lit.
     */
    overlayStyle?: React.CSSProperties;
    /**
     * Whether to draw the corner close button; off for a window that places its
     * own `DialogClose`.
     */
    showCloseButton?: boolean;
  }
>(
  (
    { className, overlayClassName, overlayStyle, showCloseButton = true, children, ...props },
    ref,
  ) => {
    const renderCloseButton = () => {
      if (!showCloseButton) return null;
      return (
        <DialogPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none">
          <XIcon
            aria-hidden
            className="h-4 w-4"
            weight="bold"
          />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      );
    };

    return (
      <DialogPortal>
        <DialogOverlay
          className={overlayClassName}
          style={overlayStyle}
        />
        {/* Centred on whole pixels where round() exists: a half-pixel centre draws everything blurred. */}
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed top-[50%] left-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border p-6 shadow-lg duration-200 supports-[top:round(1px,1px)]:top-[round(50%,1px)] supports-[top:round(1px,1px)]:left-[round(50%,1px)] supports-[top:round(1px,1px)]:translate-x-[round(-50%,1px)] supports-[top:round(1px,1px)]:translate-y-[round(-50%,1px)] sm:rounded-lg',
            className,
          )}
          {...props}
        >
          {children}
          {renderCloseButton()}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col text-center sm:text-left', className)}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
    /**
     * The face the title is set in, which sets the gap under it.
     *
     * @default 'body'
     */
    face?: TitleFace;
  }
>(({ className, face = 'body', ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg leading-none font-semibold tracking-tight last:mb-0',
      { 'font-display mb-title-display': face === 'display', 'mb-title-body': face === 'body' },
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-muted-foreground text-sm', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
